import _ from 'lodash';
import validateField from '../handlers/checkInputsAvailabilityHandler';

const requiredField = (message) => ({
  required: true,
  message: message || ''
});

const minLength = (length, message) => ({
  validator(_, value) {
    if (value === '-') {
      return Promise.resolve();
    }
    if (!value || value.length >= length) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(message || `Minimum length is ${length} characters`));
  }
});

const maxLength = (length, message) => ({
  max: length,
  message: message || `Maximum length is ${length} characters`
});

const validateName = {
  pattern: /^[A-Za-z]+$/,
  message: 'Invalid format. Only letters are allowed'
};

const passwordComplexity = {
  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,15}$/,
  message: 'invalid format'
};

const validatePasswordMatch = (compareField, getFieldValue) => ({
  validator(_, value) {
    if (!value || getFieldValue(compareField) === value) {
      return Promise.resolve();
    }
    return Promise.reject(new Error('Passwords do not match'));
  }
});

const validatePhoneAndCountryCode = (countryCode) => ({
  validator(_, value) {
    if (!value) {
      return Promise.reject(new Error('Phone number is required'));
    }
    if (!countryCode) {
      return Promise.reject(new Error('Country code is required'));
    }
    return Promise.resolve();
  }
});

const validateNumeric = {
  pattern: /^[0-9]+$/,
  message: 'Only numeric values are allowed'
};

const validatePhoneNumber = {
  pattern: /^\+?[1-9]\d{1,14}$/,
  message: 'Please enter a valid phone number'
};

const validateUsername = {
  pattern: /^(?!.*\.\.)[\w.-]+$/,
  message: 'letters, numbers, _, ., and - are allowed'
};

const validateDate = {
  pattern: /^\d{4}-\d{2}-\d{2}$/,
  message: 'Date must be in YYYY-MM-DD format'
};

const validateURL = {
  type: 'url',
  message: 'Please enter a valid URL'
};

// Checkbox Validation (at least one selected)
export const validateCheckbox = (message = 'Please select at least one option') => ({
  validator(_, value) {
    if (value && value.length > 0) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(message));
  }
});

const customPattern = (pattern, message) => ({
  pattern: pattern,
  message: message || 'Invalid input format'
});

const debouncedValidateField = (dispatch) =>
  _.debounce(async (value, fieldType, callback) => {
    if (!value) {
      return callback(``);
    }

    if (fieldType === 'email') {
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!emailRegex.test(value)) {
        callback('Please enter a valid email address');
        return;
      }
    }

    if (fieldType === 'username') {
      const usernameRegex = /^[A-Za-z0-9]{3,30}$/;

      if (value.length < 3) {
        callback('Username must be at least 3 characters long');
        return;
      }

      if (value.length > 30) {
        callback('Username cannot exceed 30 characters');
        return;
      }

      if (!usernameRegex.test(value)) {
        callback('Username can only contain letters and numbers');
        return;
      }
    }

    if (fieldType) {
      try {
        await validateField({ fieldType, value }, dispatch);
        callback();
      } catch (error) {
        callback(error.message);
      }
    } else {
      callback();
    }
  }, 500);

const validateEmail = (fieldType, dispatch) => (rule, value) =>
  new Promise((resolve, reject) => {
    debouncedValidateField(dispatch)(value, fieldType, (message) => {
      if (message) {
        reject(new Error(message));
      } else {
        resolve();
      }
    });
  });

const validateEmailInput = {
  validator: (_, value) => {
    if (!value) {
      return Promise.reject(new Error(''));
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return Promise.reject(new Error(' '));
    }

    return Promise.resolve();
  }
};

const validateUserName =
  (fieldType, dispatch, initialValue = '') =>
  (rule, value) =>
    new Promise((resolve, reject) => {
      if (value === initialValue) {
        resolve();
        return;
      }
      debouncedValidateField(dispatch)(value, fieldType, (message) => {
        if (message) {
          reject(new Error(message));
        } else {
          resolve();
        }
      });
    });

const validateContactUsPhoneNumber = {
  validator: (_, value) => {
    const phoneRegex = /^[+]?[\d\s()-]{7,14}$/;
    if (!value || phoneRegex.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(''));
  }
};
export const validateNameField = (errorMessage) => ({
  validator: (_, value) => {
    const defaultError = 'Only letters are allowed';
    const msg = errorMessage || defaultError;

    if (!/^[A-Za-z]+$/.test(value)) {
      return Promise.reject(new Error(msg));
    }

    return Promise.resolve();
  }
});

const validateOnlyLetters = (_, value) =>
  value && /^[a-zA-Z]+$/.test(value) ? Promise.resolve() : Promise.reject(new Error(''));

const validateNoLeadingSpace = {
  validator: (_, value) => {
    if (value && value.startsWith(' ')) {
      return Promise.reject(new Error('Input cannot start with a space'));
    }
    return Promise.resolve();
  }
};

export {
  validateContactUsPhoneNumber,
  validateNoLeadingSpace,
  validatePasswordMatch,
  validatePhoneNumber,
  validateOnlyLetters,
  validateEmailInput,
  passwordComplexity,
  validateUsername,
  validateUserName,
  validateNumeric,
  customPattern,
  requiredField,
  validateEmail,
  validateDate,
  validateURL,
  minLength,
  maxLength,
  validateName,
  validatePhoneAndCountryCode
};
